import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import {
  Layers,
  Filter,
  ZoomIn,
  ZoomOut,
  Home,
  Info,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { MONITORED_AREAS, ALERT_COLORS, ALERT_LEVEL_LABELS, ALERT_BG, type MonitoredArea } from '@/lib/mockData'
import type { AlertLevel } from '@/types'

// ----------------------------------------------------------
// ArcGIS map (lazy-loaded to avoid SSR issues)
// ----------------------------------------------------------

interface MapPanelProps {
  selectedArea: MonitoredArea | null
  onAreaSelect: (area: MonitoredArea | null) => void
  activeLayers: Set<string>
}

function MapPanel({ onAreaSelect, activeLayers }: MapPanelProps) {
  const mapDivRef = useRef<HTMLDivElement>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [mapError, setMapError] = useState<string | null>(null)
  const viewRef = useRef<{ destroy: () => void } | null>(null)
  const layersRef = useRef<Record<string, { visible: boolean }>>({})

  // Toggle layer visibility when activeLayers changes
  useEffect(() => {
    const layers = layersRef.current
    for (const [id, layer] of Object.entries(layers)) {
      layer.visible = activeLayers.has(id)
    }
  }, [activeLayers])

  useEffect(() => {
    if (!mapDivRef.current) return

    let cancelled = false

    async function initMap() {
      try {
        const [
          { default: Map },
          { default: MapView },
          { default: GraphicsLayer },
          { default: Graphic },
          { default: SimpleMarkerSymbol },
          { default: PopupTemplate },
        ] = await Promise.all([
          import('@arcgis/core/Map'),
          import('@arcgis/core/views/MapView'),
          import('@arcgis/core/layers/GraphicsLayer'),
          import('@arcgis/core/Graphic'),
          import('@arcgis/core/symbols/SimpleMarkerSymbol'),
          import('@arcgis/core/PopupTemplate'),
        ])

        if (cancelled || !mapDivRef.current) return

        // Create one GraphicsLayer per logical layer
        const ewLayer = new GraphicsLayer({ title: 'Score Early Warning' })
        const suscLayer = new GraphicsLayer({ title: 'Suscettibilità Frana' })
        const floodLayer = new GraphicsLayer({ title: 'Zone Alluvionali PAI' })
        const insarLayer = new GraphicsLayer({ title: 'Spostamento InSAR' })
        const changeLayer = new GraphicsLayer({ title: 'Change Detection' })

        // Store refs for toggling
        layersRef.current = {
          ew: ewLayer,
          susceptibility: suscLayer,
          flood: floodLayer,
          insar: insarLayer,
          change: changeLayer,
        }

        // Set initial visibility from activeLayers prop
        ewLayer.visible = activeLayers.has('ew')
        suscLayer.visible = activeLayers.has('susceptibility')
        floodLayer.visible = activeLayers.has('flood')
        insarLayer.visible = activeLayers.has('insar')
        changeLayer.visible = activeLayers.has('change')

        const map = new Map({
          basemap: 'dark-gray-vector',
          layers: [suscLayer, floodLayer, insarLayer, changeLayer, ewLayer],
        })

        const view = new MapView({
          container: mapDivRef.current,
          map,
          center: [12.5, 42.0],
          zoom: 6,
          ui: {
            components: ['zoom', 'attribution'],
          },
        })

        viewRef.current = view

        // --- Populate layers ---

        // EW Score: colored circles per monitored area
        MONITORED_AREAS.forEach((area) => {
          const color = ALERT_COLORS[area.level]
          ewLayer.add(new Graphic({
            geometry: { type: 'point' as const, longitude: area.lon, latitude: area.lat },
            symbol: new SimpleMarkerSymbol({
              style: 'circle',
              color: color + 'cc',
              size: area.ewScore > 7 ? 14 : area.ewScore > 5 ? 11 : 8,
              outline: { color, width: 1.5 },
            }),
            attributes: { ...area },
            popupTemplate: new PopupTemplate({
              title: '{name}',
              content: '<b>Score EW:</b> {ewScore}<br/><b>Livello:</b> {level}<br/><b>Popolazione:</b> {population}<br/><b>Aggiornamento:</b> {lastUpdate}',
            }),
          }))
        })

        // Susceptibility: large transparent orange circles around high-risk areas
        MONITORED_AREAS.forEach((area) => {
          const opacity = Math.min(0.35, area.ewScore / 25)
          suscLayer.add(new Graphic({
            geometry: { type: 'point' as const, longitude: area.lon, latitude: area.lat },
            symbol: new SimpleMarkerSymbol({
              style: 'circle',
              color: [249, 115, 22, opacity * 255],
              size: 28 + area.ewScore * 2,
              outline: { color: [249, 115, 22, 80], width: 1 },
            }),
          }))
        })

        // Flood zones: blue circles on flood-prone areas (score > 4 in plains)
        MONITORED_AREAS.filter(a => a.areaKm2 > 100).forEach((area) => {
          floodLayer.add(new Graphic({
            geometry: { type: 'point' as const, longitude: area.lon, latitude: area.lat },
            symbol: new SimpleMarkerSymbol({
              style: 'circle',
              color: [59, 130, 246, 50],
              size: 32 + area.areaKm2 / 30,
              outline: { color: [59, 130, 246, 100], width: 1.5 },
            }),
          }))
        })

        // InSAR displacement: small diamonds at each area
        MONITORED_AREAS.forEach((area) => {
          const vel = -5 - area.ewScore * 2.5
          insarLayer.add(new Graphic({
            geometry: { type: 'point' as const, longitude: area.lon + 0.03, latitude: area.lat + 0.02 },
            symbol: new SimpleMarkerSymbol({
              style: 'diamond',
              color: vel < -15 ? [168, 85, 247, 200] : [168, 85, 247, 120],
              size: 7,
              outline: { color: [168, 85, 247, 255], width: 1 },
            }),
            popupTemplate: new PopupTemplate({
              title: `InSAR — ${area.name}`,
              content: `<b>Velocità:</b> ${vel.toFixed(1)} mm/anno`,
            }),
          }))
        })

        // Change Detection: squares on areas with high EW
        MONITORED_AREAS.filter(a => a.ewScore > 5).forEach((area) => {
          changeLayer.add(new Graphic({
            geometry: { type: 'point' as const, longitude: area.lon - 0.03, latitude: area.lat - 0.02 },
            symbol: new SimpleMarkerSymbol({
              style: 'square',
              color: [6, 182, 212, 100],
              size: 9,
              outline: { color: [6, 182, 212, 200], width: 1 },
            }),
            popupTemplate: new PopupTemplate({
              title: `Change Detection — ${area.name}`,
              content: `<b>Variazione:</b> rilevata<br/><b>Score EW:</b> ${area.ewScore}`,
            }),
          }))
        })

        await view.whenLayerView(ewLayer)

        if (!cancelled) setMapLoaded(true)

        // Click handler
        view.on('click', (event) => {
          view.hitTest(event).then((response) => {
            const result = response.results[0]
            if (result && 'graphic' in result && result.graphic?.attributes?.id) {
              const found = MONITORED_AREAS.find((a) => a.id === result.graphic.attributes.id)
              onAreaSelect(found ?? null)
            } else {
              onAreaSelect(null)
            }
          })
        })
      } catch (err) {
        if (!cancelled) {
          console.warn('ArcGIS map initialization error:', err)
          setMapError('Mappa non disponibile in questa configurazione. Verifica la licenza ArcGIS.')
        }
      }
    }

    initMap()

    return () => {
      cancelled = true
      if (viewRef.current) {
        viewRef.current.destroy()
        viewRef.current = null
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAreaSelect])

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden">
      {/* ArcGIS container */}
      <div ref={mapDivRef} className="w-full h-full" />

      {/* Loading overlay */}
      {!mapLoaded && !mapError && (
        <div className="absolute inset-0 bg-terra-dark/90 flex flex-col items-center justify-center gap-3">
          <div className="w-8 h-8 border-2 border-terra-blue/30 border-t-terra-blue rounded-full animate-spin" />
          <p className="text-sm text-slate-400">Caricamento mappa...</p>
        </div>
      )}

      {/* Error / No-license fallback */}
      {mapError && (
        <div className="absolute inset-0 bg-terra-dark/95 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="w-12 h-12 rounded-full bg-terra-orange/20 border border-terra-orange/40 flex items-center justify-center mx-auto mb-4">
              <Info size={20} className="text-terra-orange" />
            </div>
            <p className="text-sm text-slate-300 font-medium mb-2">Mappa non disponibile</p>
            <p className="text-xs text-slate-500">{mapError}</p>
            {/* Fallback SVG Italy map sketch */}
            <div className="mt-6 opacity-30">
              <svg viewBox="0 0 200 280" className="w-32 mx-auto fill-slate-700">
                <path d="M80,20 L95,18 L110,22 L118,35 L115,50 L105,60 L100,75 L95,90 L98,105 L105,118 L110,130 L115,145 L112,160 L105,172 L95,182 L85,192 L78,205 L74,218 L70,230 L62,240 L55,248 L50,255 L48,260 L52,262 L58,258 L65,248 L73,240 L80,232 L90,225 L100,218 L108,210 L115,200 L120,188 L122,175 L118,162 L112,150 L108,138 L104,124 L100,110 L98,96 L102,82 L108,68 L116,55 L120,42 L118,28 L112,18 L100,12 L88,14 Z" />
              </svg>
              <p className="text-xs text-slate-600 mt-2">Demo senza licenza ArcGIS</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ----------------------------------------------------------
// Layer controls
// ----------------------------------------------------------

const LAYER_COLORS: Record<string, string> = {
  susceptibility: '#f97316',
  flood: '#3b82f6',
  insar: '#a855f7',
  ew: '#ef4444',
  change: '#06b6d4',
}

const LAYERS = [
  { id: 'susceptibility', label: 'Suscettibilità Frana', active: true },
  { id: 'flood', label: 'Zone Alluvionali PAI', active: true },
  { id: 'insar', label: 'Spostamento InSAR', active: false },
  { id: 'ew', label: 'Score Early Warning', active: true },
  { id: 'change', label: 'Change Detection', active: false },
]

// ----------------------------------------------------------
// Area detail panel
// ----------------------------------------------------------

function AreaDetailPanel({
  area,
  onClose,
}: {
  area: MonitoredArea
  onClose: () => void
}) {
  const color = ALERT_COLORS[area.level]

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.25 }}
      className="glass-strong rounded-xl p-4 w-72"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-white">{area.name}</h3>
          <p className="text-xs text-slate-400">{area.province} · {area.region}</p>
        </div>
        <button
          onClick={onClose}
          className="text-slate-500 hover:text-slate-300 transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      {/* EW Score gauge */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/60 border mb-3"
        style={{ borderColor: color + '40' }}
      >
        <div className="text-center">
          <div className="text-2xl font-bold" style={{ color }}>
            {area.ewScore.toFixed(1)}
          </div>
          <div className="text-xs text-slate-500">EW Score</div>
        </div>
        <div className="flex-1">
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${(area.ewScore / 10) * 100}%`,
                background: `linear-gradient(90deg, ${color}66, ${color})`,
              }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-slate-600">0</span>
            <span className="text-xs" style={{ color }}>{ALERT_LEVEL_LABELS[area.level]}</span>
            <span className="text-xs text-slate-600">10</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {[
          { label: 'Popolazione', value: area.population.toLocaleString('it-IT') },
          { label: 'Superficie', value: `${area.areaKm2} km²` },
          { label: 'Lat', value: area.lat.toFixed(4) },
          { label: 'Lon', value: area.lon.toFixed(4) },
        ].map(({ label, value }) => (
          <div key={label} className="bg-slate-900/60 rounded-lg p-2">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-xs font-medium text-white mt-0.5">{value}</div>
          </div>
        ))}
      </div>

      {/* Level badge */}
      <div className={cn('flex items-center justify-center py-2 rounded-lg border text-sm font-bold', ALERT_BG[area.level])}>
        Allerta {area.level} — {ALERT_LEVEL_LABELS[area.level]}
      </div>
    </motion.div>
  )
}

// ----------------------------------------------------------
// Legend
// ----------------------------------------------------------

function MapLegend() {
  const levels: AlertLevel[] = ['P1', 'P2', 'P3', 'P4']
  return (
    <div className="glass-strong rounded-lg p-3">
      <p className="text-xs font-semibold text-slate-300 mb-2">Legenda Rischio</p>
      <div className="space-y-1.5">
        {levels.map((l) => (
          <div key={l} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ background: ALERT_COLORS[l] }}
            />
            <span className="text-xs text-slate-400">{l} — {ALERT_LEVEL_LABELS[l]}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ----------------------------------------------------------
// Page
// ----------------------------------------------------------

export default function RiskMapPage() {
  const [selectedArea, setSelectedArea] = useState<MonitoredArea | null>(null)
  const [activeLayers, setActiveLayers] = useState<Set<string>>(
    new Set(LAYERS.filter((l) => l.active).map((l) => l.id))
  )
  const [showLayers, setShowLayers] = useState(false)

  function toggleLayer(id: string) {
    setActiveLayers((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-8rem)]">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between flex-shrink-0"
      >
        <div>
          <h1 className="text-xl font-bold text-white">Mappa Rischio Idrogeologico</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {MONITORED_AREAS.length} aree monitorate · ArcGIS Enterprise
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Layer toggle */}
          <div className="relative">
            <button
              onClick={() => setShowLayers((s) => !s)}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all',
                showLayers
                  ? 'bg-terra-blue/20 border border-terra-blue/40 text-terra-blue'
                  : 'glass border border-terra-blue/20 text-slate-300 hover:text-white'
              )}
            >
              <Layers size={14} />
              Layer
            </button>

            {showLayers && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute right-0 top-full mt-2 w-56 z-50 glass-strong rounded-xl p-3 shadow-2xl"
              >
                <p className="text-xs font-semibold text-slate-300 mb-2 flex items-center gap-1.5">
                  <Filter size={11} /> Layer Attivi
                </p>
                <div className="space-y-1">
                  {LAYERS.map((layer) => (
                    <button
                      key={layer.id}
                      onClick={() => toggleLayer(layer.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-800/60 transition-colors"
                    >
                      <div
                        className="w-3 h-3 rounded-sm flex-shrink-0 transition-all"
                        style={{
                          backgroundColor: activeLayers.has(layer.id)
                            ? LAYER_COLORS[layer.id]
                            : '#334155',
                        }}
                      />
                      <span className={cn(
                        'text-xs text-left transition-colors',
                        activeLayers.has(layer.id) ? 'text-slate-200' : 'text-slate-500'
                      )}>{layer.label}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          <button className="btn-ghost text-sm px-3 py-2">
            <ZoomIn size={14} />
          </button>
          <button className="btn-ghost text-sm px-3 py-2">
            <ZoomOut size={14} />
          </button>
          <button className="btn-ghost text-sm px-3 py-2">
            <Home size={14} />
          </button>
        </div>
      </motion.div>

      {/* Map + panels layout */}
      <div className="relative flex-1 flex gap-4 min-h-0">
        {/* Map */}
        <div className="flex-1 min-w-0">
          <MapPanel selectedArea={selectedArea} onAreaSelect={setSelectedArea} activeLayers={activeLayers} />
        </div>

        {/* Side panels */}
        <div className="flex flex-col gap-3 w-72 flex-shrink-0 overflow-y-auto">
          {/* Area detail */}
          {selectedArea ? (
            <AreaDetailPanel
              area={selectedArea}
              onClose={() => setSelectedArea(null)}
            />
          ) : (
            <div className="glass-strong rounded-xl p-4">
              <p className="text-xs text-slate-500 text-center py-4">
                Clicca su un'area nella mappa per visualizzare i dettagli
              </p>
            </div>
          )}

          {/* Legend */}
          <MapLegend />

          {/* Quick stats */}
          <div className="glass-strong rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-300 mb-3">Riepilogo Livelli</p>
            <div className="space-y-2">
              {(['P4', 'P3', 'P2', 'P1'] as AlertLevel[]).map((level) => {
                const count = MONITORED_AREAS.filter((a) => a.level === level).length
                return (
                  <div key={level} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: ALERT_COLORS[level] }} />
                    <span className="text-xs text-slate-400 flex-1">{ALERT_LEVEL_LABELS[level]}</span>
                    <span className="text-xs font-bold text-white">{count}</span>
                    <div className="w-16 h-1 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(count / MONITORED_AREAS.length) * 100}%`,
                          background: ALERT_COLORS[level],
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
